import { Injectable } from '@angular/core';
import { HttpClient, HttpContext, HttpEvent } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CustomHttpParams } from '@core/services/request/model/request.model';
import { environment } from '@env/environment';
import { LOADING_CXT_TOKEN } from '@core/interceptors/loading.interceptor';
import { HIDE_ERROR_ALERT_CXT_TOKEN } from '@core/interceptors/error.interceptor';
import { PlatformLocation } from '@angular/common';

@Injectable({ providedIn: 'root' })
export class RequestService {
  constructor(
    private readonly _http: HttpClient,
    private readonly _platformLocation: PlatformLocation
  ) {}

  public getAsset<T>(asset: string, options: CustomHttpParams = {}): Observable<T> {
    return this._http.get<T>(`${this._platformLocation.getBaseHrefFromDOM()}assets/${asset}`, {
      ...options,
      context: this.allContext(options),
    });
  }

  /**
   * Get method
   */
  public get<RequestModel>(url: string, options: CustomHttpParams = {}): Observable<RequestModel> {
    url = `${environment.baseUrl}/${url}`;
    return this._http.get<RequestModel>(url, {
      ...options,
      context: this.allContext(options),
    });
  }

  /**
   * Download method
   */
  public download(url: string, options: CustomHttpParams = {}): Observable<Blob> {
    url = `${environment.baseUrl}/${url}`;
    return this._http.get(url, {
      ...options,
      context: this.allContext(options),
      responseType: 'blob',
    });
  }

  /**
   * Download method with progress
   */
  public downloadWithProgress(url: string, options: CustomHttpParams = {}): Observable<HttpEvent<Blob>> {
    url = `${environment.baseUrl}/${url}`;
    return this._http.get(url, {
      ...options,
      context: this.allContext(options),
      reportProgress: true,
      observe: 'events',
      responseType: 'blob',
    });
  }

  /**
   * Download method with progress
   */
  public downloadWithProgressPost<T>(url: string, request: T, options: CustomHttpParams = {}): Observable<HttpEvent<Blob>> {
    url = `${environment.baseUrl}/${url}`;
    return this._http.post(url, request, {
      ...options,
      context: this.allContext(options),
      reportProgress: true,
      observe: 'events',
      responseType: 'blob',
    });
  }
  /**
   * Post method
   */
  public post<RequestModel, RequestBody>(url: string, body: RequestBody, options: CustomHttpParams = {}): Observable<RequestModel> {
    url = `${environment.baseUrl}/${url}`;
    return this._http.post<RequestModel>(url, body, {
      ...options,
      context: this.allContext(options),
    });
  }

  /**
   * Put method
   */
  public put<RequestModel, RequestBody>(url: string, body: RequestBody, options: CustomHttpParams = {}): Observable<RequestModel> {
    url = `${environment.baseUrl}/${url}`;
    return this._http.put<RequestModel>(url, body, {
      ...options,
      context: this.allContext(options),
    });
  }

  /**
   * Delete method
   */
  public delete<RequestModel>(url: string, options: CustomHttpParams = {}): Observable<RequestModel> {
    url = `${environment.baseUrl}/${url}`;
    return this._http.delete<RequestModel>(url, {
      ...options,
      context: this.allContext(options),
    });
  }

  private allContext(options: CustomHttpParams = {}): HttpContext {
    const context = this.loadingContext(options.loading);
    return this.hideErrorContext(options.hideErrorAlert, context);
  }

  private hideErrorContext(hideErrorAlert?: boolean, context: HttpContext = new HttpContext()): HttpContext {
    if (!hideErrorAlert) {
      return context;
    }
    return context.set(HIDE_ERROR_ALERT_CXT_TOKEN, hideErrorAlert);
  }

  private loadingContext(loading?: string | boolean, context: HttpContext = new HttpContext()): HttpContext {
    if (loading == null) {
      return context;
    }
    return context.set(LOADING_CXT_TOKEN, loading);
  }
}
